import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import {
  fetchReportApplicationList,
  fetchReportApplicationMember,
  fetchReportPrint,
  fetchReportWorkStyle,
} from '../../../../states/containers/analyze/analyzeSlice'
import {
  updateCurrentAnalyzeSelectDate,
  updateCurrentAnalyzeSelectMember,
  updateCurrentAnalyzeSelectTeam,
} from '../../../../states/containers/common/currentSlice'
import { fetchMemberList } from '../../../../states/containers/member/listSlice'
import { RootState } from '../../../../states/containers/store'
import { LangValue } from '../../../../states/types/common'
import { endOfMonth, format, nowDate, prevMonth, startOfMonth, yesterDay } from '../../../../utils/date'
import { getGetPrams, setPath, setUrlParam } from '../../../../utils/url'
import DateModule from '../../../components/analyze/DateModule'
import Application from '../../../components/analyze/report/Application'
import MemberApplication from '../../../components/analyze/report/MemberApplication'
import Print from '../../../components/analyze/report/Print'
import WorkStyle from '../../../components/analyze/report/WorkStyle'
import SearchConditionModule from '../../../components/analyze/SearchConditionModule'
import SearchModule from '../../../components/analyze/SearchModule'
import Loading from '../../../components/common/Loading'
import ANALYZE_REPORT from '../../../constants/analyzeReport'
import { URL } from '../../../constants/url'
import PdfModule from '../../../components/common/PdfModule'

interface GetParams {
  date: string
  team_ids: string
  member_ids: string
}

interface RequestParams {
  start_date: string,
  end_date: string,
  team_ids?: number[]
  member_ids?: number[]
}

const Detail: FC = () => {
  const { type } = useParams<{ type: string | undefined }>()
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const {
    analyze,
    current,
  } = useSelector((state: RootState) => state)

  const [date, setDate] = useState<string>("")
  const [teamIds, setTeamIds] = useState<number[]>([])
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [isClear, setIsClear] = useState<boolean>(false)

  const [isDisablePdf, setIsDisablePdf] = useState<boolean>(true)

  const title = useMemo(() => (
    ANALYZE_REPORT.find((value) => value.key === type)?.title
  ), [type])

  const endDate = useMemo(() => {
    if (Number(format(date, "YYYYMM")) === Number(nowDate("YYYYMM"))) {
      return yesterDay("YYYY-MM-DD")
    }
    return endOfMonth(date, "YYYY-MM-DD")
  }, [date])

  const fetchData = (params: RequestParams) => {
    if (type === "print") {
      dispatch(fetchReportPrint(params))
      return
    }
    if (type === "work_style") {
      dispatch(fetchReportWorkStyle(params))
      return
    }
    if (type === "application") {
      dispatch(fetchReportApplicationList(params))
      return
    }
    if (type === "member_application") {
      dispatch(fetchReportApplicationMember(params))
    }
  }

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.date) {
      setDate(format(getParams.date))
    } else if (current.analyzeSelectDate) {
      setDate(format(current.analyzeSelectDate))
    } else if ((Number(startOfMonth(nowDate(), "YYYYMMDD")) + 7) < Number(nowDate("YYYYMMDD"))) {
      setDate(startOfMonth(nowDate(), "YYYY-MM-DD"))
    } else {
      setDate(startOfMonth(prevMonth(nowDate()), "YYYY-MM-DD"))
    }
    if (getParams.team_ids) {
      setTeamIds(getParams.team_ids.split(',').map((value) => Number(value)))
    } else if (current.analyzeSelectTeam) {
      setTeamIds(current.analyzeSelectTeam.split(',').map((value) => Number(value)))
    }
    if (getParams.member_ids) {
      setMemberIds(getParams.member_ids.split(',').map((value) => Number(value)))
    } else if (current.analyzeSelectMember) {
      setMemberIds(current.analyzeSelectMember.split(',').map((value) => Number(value)))
    }
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!date) return
    const params: RequestParams = {
      start_date: startOfMonth(date, "YYYY-MM-DD"),
      end_date: endDate,
    }
    const query = [`date=${startOfMonth(date, "YYYY-MM-DD")}`]
    if (teamIds.length) {
      params.team_ids = teamIds
      query.push(`team_ids=${teamIds}`)
    }
    if (memberIds.length) {
      params.member_ids = memberIds
      query.push(`member_ids=${memberIds}`)
    }
    dispatch(updateCurrentAnalyzeSelectDate(date))
    dispatch(updateCurrentAnalyzeSelectTeam(teamIds.join(',')))
    dispatch(updateCurrentAnalyzeSelectMember(memberIds.join(',')))
    fetchData(params)
    history.push(`${setPath(setUrlParam(
      URL.ANALYZE_REPORT_DETAIL.path,
      [
        { key: 'type', value: type || "" },
      ],
    ), language)}?${query.join('&')}`)
  }, [date, teamIds, memberIds, type])

  useEffect(() => {
    dispatch(
      fetchMemberList(),
    )
  }, [])

  useEffect(() => {
    if (!isClear) return
    setIsClear(false)
  }, [isClear])

  useEffect(() => {
    if (analyze.isLoading) {
      setIsDisablePdf(true)
      return
    }
    setTimeout(() => {
      setIsDisablePdf(false)
    }, 5000)
  }, [analyze])

  return (
    <section className="analyze_page">
      <Helmet>
        <title>{`Qasee - 組織分析 - 分析レポート - ${title}`}</title>
      </Helmet>
      <section className="breadcrumb">
        <Link
          to={setPath(URL.ANALYZE.path, language)}
        >
          組織分析
        </Link>
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.ANALYZE_REPORT.path, language)}
        >
          分析レポート
        </Link>
        &nbsp;&gt;&nbsp;
        {title}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {title}
        </h1>
        <div className="tools">
          <SearchModule
            callbackTeamIds={setTeamIds}
            callbackMemberIds={setMemberIds}
            isClear={isClear}
          />
          <DateModule
            date={date}
            changeCallback={(callback_date) => {
              setDate(callback_date)
            }}
            startMonth={["print", "work_style"].includes(type || "") ? "202205" : ""}
          />
        </div>
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            title={`Qasee分析レポート ${title} ${startOfMonth(date, "YYYY年MM月DD日")} ~ ${format(endDate, "YYYY年MM月DD日")}`}
            target="pdf_area"
            disabled={isDisablePdf}
          />
        </div>
      </section>
      <section id="pdf_area">
        <SearchConditionModule
          name={`分析レポート：${title}`}
          date={date}
          teamIds={teamIds}
          memberIds={memberIds}
        />
        {
          analyze.isLoading ? (
            <>
              <Loading />
            </>
          ) : (
            <>
              {
                type === "print" && analyze.reportPrint && (
                  <Print
                    date={date}
                    list={analyze.reportPrint}
                  />
                )
              }
              {
                type === "work_style" && analyze.reportWorkStyle && (
                  <WorkStyle
                    date={date}
                    list={analyze.reportWorkStyle}
                  />
                )
              }
              {
                type === "application" && analyze.reportApplicationList && (
                  <Application
                    date={date}
                    list={analyze.reportApplicationList}
                  />
                )
              }
              {
                type === "member_application" && analyze.reportApplicationMember && (
                  <MemberApplication
                    date={date}
                    list={analyze.reportApplicationMember}
                  />
                )
              }
            </>
          )
        }
      </section>
    </section>
  )
}

export default Detail
